import React, { useRef, useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Camera, Loader2, ZoomIn, ZoomOut, ImagePlus, Move, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

async function getCroppedBlob(imageSrc, crop) {
  const image = await createImage(imageSrc);
  const MAX = 1600; // longest cropped edge — sharp on phone & web, ~2x lighter than 1920
  const scale = Math.min(1, MAX / crop.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(crop.width * scale);
  canvas.height = Math.round(crop.height * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
}

// Lot 624 — « Garder telle quelle » : photo entière (sans recadrage), simplement réduite à 1600 px sur son grand côté.
async function getFullBlob(imageSrc) {
  const image = await createImage(imageSrc);
  const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);
  canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
}

const readFileAsDataURL = (fileOrBlob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(fileOrBlob);
  });

// Facebook-style ad formats (landscape / square / portrait) for the ad image cropper.
export const AD_ASPECTS = [
  { key: "landscape", labelKey: "ads.aspect_landscape", label: "Paysage", value: 16 / 9 },
  { key: "square", labelKey: "ads.aspect_square", label: "Carré", value: 1 },
  { key: "portrait", labelKey: "ads.aspect_portrait", label: "Portrait", value: 4 / 5 },
  { key: "reels", labelKey: "ads.aspect_reels", label: "Reels 9:16", value: 9 / 16 },
];

// Shared crop dialog (drag + zoom, FB-style). When `aspectOptions` is passed it
// shows a format selector (auto-detected default) instead of a locked ratio.
const CropDialog = ({ src, aspect, aspectOptions, cropShape, title, busy, progress = 0, onCancel, onConfirm, onKeep = null, testId }) => {
  const { t } = useApp();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [activeAspect, setActiveAspect] = useState(aspect);
  const onCropComplete = useCallback((_, px) => setAreaPixels(px), []);

  // Auto-pick the format closest to the source image's own orientation so a
  // portrait photo defaults to Portrait (no forced horizontal crop).
  useEffect(() => {
    if (!aspectOptions || !src) return;
    let alive = true;
    createImage(src).then((im) => {
      if (!alive || !im.naturalWidth || !im.naturalHeight) return;
      const r = im.naturalWidth / im.naturalHeight;
      const best = aspectOptions.reduce((a, b) => (Math.abs(b.value - r) < Math.abs(a.value - r) ? b : a));
      setActiveAspect(best.value);
      setCrop({ x: 0, y: 0 }); setZoom(1);
    }).catch(() => {});
    return () => { alive = false; };
  }, [src, aspectOptions]);

  const usedAspect = aspectOptions ? activeAspect : aspect;

  return (
    <Dialog open={!!src} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      {/* z-[170] : le recadrage s'ouvre par-dessus TOUS les dialogues (feuille « Complète ton profil » z-[150], son sélecteur pays z-[160]) — il ne doit jamais passer derrière. */}
      <DialogContent className="max-w-lg z-[170]" overlayClassName="z-[170]" data-testid={`${testId}-modal`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t("crop.drag_hint", "Fais glisser l'image pour la repositionner, utilise le curseur pour zoomer.")}</DialogDescription>
        </DialogHeader>
        {aspectOptions && (
          <div className="flex items-center justify-center gap-2" data-testid={`${testId}-aspect-row`}>
            {aspectOptions.map((o) => {
              const on = Math.abs(usedAspect - o.value) < 0.001;
              return (
                <button key={o.key} type="button" onClick={() => { setActiveAspect(o.value); setCrop({ x: 0, y: 0 }); setZoom(1); }}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
                  style={on ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "transparent" } : { borderColor: "var(--yalola-border)", color: "var(--yalola-text-muted)", background: "transparent" }}
                  data-testid={`${testId}-aspect-${o.key}`}>{t(o.labelKey, o.label)}</button>
              );
            })}
          </div>
        )}
        <div className="relative w-full rounded-xl overflow-hidden" style={{ height: 340, background: "#141210" }}>
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={usedAspect}
              cropShape={cropShape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3 px-1">
          <ZoomOut size={16} style={{ color: "var(--yalola-text-muted)" }} />
          <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={([v]) => setZoom(v)} className="flex-1" data-testid={`${testId}-zoom`} />
          <ZoomIn size={16} style={{ color: "var(--yalola-text-muted)" }} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid={`${testId}-cancel`}>{t("common.cancel", "Annuler")}</Button>
          {onKeep && (
            <Button variant="outline" onClick={onKeep} disabled={busy} data-testid={`${testId}-keep`} title={t("crop.keep_hint", "Photo entière, sans recadrage")}>
              <ImagePlus size={14} className="mr-1" /> {t("crop.keep", "Garder telle quelle")}
            </Button>
          )}
          <Button className="pill-primary" onClick={() => areaPixels && onConfirm(areaPixels)} disabled={busy || !areaPixels} data-testid={`${testId}-confirm`}>
            {busy ? <><Loader2 size={14} className="animate-spin mr-1" /> {progress > 0 && progress < 100 ? t("crop.uploading_pct", "Envoi {n} %").replace("{n}", progress) : t("crop.uploading", "Envoi…")}</> : t("common.save", "Enregistrer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const uploadCroppedBlob = async (src, areaPixels, onProgress) => {
  const blob = areaPixels ? await getCroppedBlob(src, areaPixels) : await getFullBlob(src); // areaPixels null = telle quelle
  const fd = new FormData();
  fd.append("file", new File([blob], "photo.jpg", { type: "image/jpeg" }));
  const { data } = await api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
  });
  return data.url;
};

/**
 * Simple picker: choose a file → crop/zoom modal → upload. Used for avatar & admin images.
 */
export const ImageCropUpload = ({
  aspect = 1,
  aspectOptions = null,
  cropShape = "rect",
  onUploaded,
  buttonLabel = "",
  icon: Icon = Camera,
  testId = "crop-upload",
  title = "",
  disabled = false,
  primary = false,
  multiple = false, // lot 622 — sélection de plusieurs photos : recadrage l'une après l'autre (« 1/3 »), onUploaded par photo
  max = Infinity, // lot 623 — photos encore autorisées (limite admin) : au-delà, les fichiers en trop sont ignorés + toast
  onLimit, // (ignorés, max) → message à afficher
  keepOriginal = false, // lot 624 — bouton « Garder telle quelle » (photo entière, sans recadrage) : plusieurs photos plus vite
}) => {
  const { t } = useApp();
  const inputRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [queue, setQueue] = useState({ rest: [], idx: 0, total: 0 });

  const pick = async (e) => {
    let files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (files.length > max) { onLimit?.(files.length - max, max); files = files.slice(0, Math.max(0, max)); }
    if (!files.length) return;
    setQueue(multiple ? { rest: files.slice(1), idx: 1, total: files.length } : { rest: [], idx: 0, total: 0 });
    setSrc(await readFileAsDataURL(files[0]));
  };

  const closeAll = () => { setSrc(null); setQueue({ rest: [], idx: 0, total: 0 }); };

  const advance = async () => {
    if (!queue.rest.length) { closeAll(); return; }
    const [next, ...rest] = queue.rest;
    setQueue((q) => ({ rest, idx: q.idx + 1, total: q.total }));
    setSrc(await readFileAsDataURL(next));
  };

  const confirm = async (areaPixels = null) => {
    setBusy(true); setProgress(0);
    try {
      const url = await uploadCroppedBlob(src, areaPixels, setProgress);
      onUploaded?.(url);
      await advance();
    } catch (e2) {
      toast.error(e2?.friendlyMessage || t("crop.upload_error", "Erreur lors de l'envoi"));
    } finally { setBusy(false); setProgress(0); }
  };

  const baseTitle = title || t("crop.title_photo", "Ajuster la photo");
  const dialogTitle = queue.total > 1 ? `${baseTitle} (${queue.idx}/${queue.total})` : baseTitle;

  const iconOnly = buttonLabel === "";
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple || undefined} onChange={pick} className="hidden" data-testid={`${testId}-input`} data-multiple={multiple ? "1" : undefined} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className={iconOnly
          ? "inline-flex items-center justify-center h-9 w-9 rounded-full shadow-md hover:opacity-90 border-2 border-white"
          : `inline-flex items-center gap-1.5 rounded-full font-medium hover:opacity-80 shadow-sm ${primary ? "px-4 py-2 text-sm font-semibold" : "px-3 py-1.5 text-xs"}`}
        style={{ background: iconOnly || primary ? "var(--yalola-primary)" : "var(--yalola-surface-alt)", color: iconOnly || primary ? "#fff" : "var(--yalola-text)" }}
        data-testid={`${testId}-btn`}
        title={iconOnly ? t("crop.change_photo", "Changer la photo") : undefined}
      >
        <Icon size={iconOnly ? 16 : 14} />
        {!iconOnly && buttonLabel}
      </button>
      {src && <CropDialog src={src} aspect={aspect} aspectOptions={aspectOptions} cropShape={cropShape} title={dialogTitle} busy={busy} progress={progress} onCancel={() => !busy && closeAll()} onConfirm={confirm} onKeep={keepOriginal ? () => confirm(null) : null} testId={testId} />}
    </>
  );
};

/**
 * Facebook-style cover menu: Import a photo / Reposition (re-crop current) / Remove.
 */
export const CoverEditMenu = ({ currentUrl, onUploaded, onRemove, testId = "cover-edit", aspect = 2.7 }) => {
  const { t } = useApp();
  const inputRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setSrc(await readFileAsDataURL(f));
  };

  const reposition = async () => {
    if (!currentUrl) return;
    try {
      // Fetch via the same-origin byte proxy (CORS-safe) so the canvas crop
      // can read the image — the normal /files/ URL 307-redirects to the R2/S3
      // CDN which blocks a cross-origin blob read.
      const fetchUrl = currentUrl.includes("/api/files/")
        ? currentUrl.replace("/api/files/", "/api/files/proxy/")
        : currentUrl;
      const resp = await fetch(fetchUrl, { mode: "cors" });
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      setSrc(await readFileAsDataURL(blob));
    } catch {
      toast.error(t("crop.load_error", "Impossible de charger l'image actuelle"));
    }
  };

  const confirm = async (areaPixels) => {
    setBusy(true); setProgress(0);
    try {
      const url = await uploadCroppedBlob(src, areaPixels, setProgress);
      onUploaded?.(url);
      setSrc(null);
    } catch (e2) {
      toast.error(e2?.friendlyMessage || t("crop.upload_error", "Erreur lors de l'envoi"));
    } finally { setBusy(false); setProgress(0); }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testId}-input`} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold hover:opacity-90 shadow-md bg-white"
            style={{ color: "var(--yalola-text)" }}
            data-testid={`${testId}-btn`}
          >
            <Camera size={14} /> {t("crop.cover_edit", "Modifier la couverture")}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid={`${testId}-menu`}>
          <DropdownMenuItem onClick={() => inputRef.current?.click()} data-testid={`${testId}-import`}>
            <ImagePlus size={14} className="mr-2" /> {t("crop.import_photo", "Importer une photo")}
          </DropdownMenuItem>
          {currentUrl && (
            <DropdownMenuItem onClick={reposition} data-testid={`${testId}-reposition`}>
              <Move size={14} className="mr-2" /> {t("crop.reposition", "Repositionner")}
            </DropdownMenuItem>
          )}
          {currentUrl && onRemove && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onRemove} className="text-red-600 focus:text-red-600" data-testid={`${testId}-remove`}>
                <Trash2 size={14} className="mr-2" /> {t("common.delete", "Supprimer")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {src && <CropDialog src={src} aspect={aspect} cropShape="rect" title={t("crop.title_cover", "Ajuster la photo de couverture")} busy={busy} progress={progress} onCancel={() => !busy && setSrc(null)} onConfirm={confirm} testId={testId} />}
    </>
  );
};

export default ImageCropUpload;
